import { useEffect, useState } from 'react';
import MobileFrame from '@/components/MobileFrame';
import { Icon } from '@/components/Icons';
import { useT } from '@/lib/i18n';
import { getProfile, updateProfile, type UserProfile } from '@/lib/auth-api';

export default function ProfileScreen() {
    const t = useT();
    const [profile, setProfile] = useState<UserProfile | null>(null);
    const [phone, setPhone] = useState('');
    const [address, setAddress] = useState('');
    const [city, setCity] = useState('');
    const [province, setProvince] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState<string | null>(null);
    const [err, setErr] = useState<string | null>(null);

    useEffect(() => {
        getProfile()
            .then((p) => {
                setProfile(p);
                setPhone(p.user.phone ?? '');
                setAddress(p.registration?.address ?? '');
                setCity(p.registration?.city ?? '');
                setProvince(p.registration?.province ?? '');
            })
            .catch((e: Error) => setErr(e.message))
            .finally(() => setLoading(false));
    }, []);

    const handleSave = async () => {
        try {
            setSaving(true);
            setErr(null);
            setMsg(null);
            const patch: Record<string, string> = {};
            if (phone && phone !== profile?.user.phone) patch.phone = phone;
            if (address !== profile?.registration?.address) patch.address = address;
            if (city !== profile?.registration?.city) patch.city = city;
            if (province !== profile?.registration?.province) patch.province = province;

            if (Object.keys(patch).length === 0) {
                setMsg('Tidak ada perubahan.');
                return;
            }

            const updated = await updateProfile(patch);
            setProfile(updated);
            setMsg('Profil berhasil diperbarui.');
        } catch (e) {
            setErr(e instanceof Error ? e.message : t.somethingWrong);
        } finally {
            setSaving(false);
        }
    };

    return (
        <MobileFrame showBack>
            <div
                className="fade-in"
                style={{ flex: 1, padding: '8px 24px 28px', display: 'flex', flexDirection: 'column' }}
            >
                <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 16 }}>Profil</h1>

                {loading && <p style={{ color: 'var(--ink-500)' }}>Memuat…</p>}

                {profile && (
                    <>
                        {/* Read-only section */}
                        <div className="card" style={{ padding: 16, marginBottom: 16 }}>
                            <KV k="Nama Lengkap" v={profile.user.fullname} />
                            <KV k="NIK" v={profile.registration?.nik ?? '—'} />
                            <KV k="Email" v={profile.user.email ?? '—'} />
                            <KV
                                k="Tempat / Tgl Lahir"
                                v={`${profile.user.placeBirth ?? '—'}, ${profile.user.dateBirth ?? '—'}`}
                            />
                            <KV
                                k="Jenis Kelamin"
                                v={
                                    profile.user.gender === 'MALE'
                                        ? t.genderMale
                                        : profile.user.gender === 'FEMALE'
                                          ? t.genderFemale
                                          : '—'
                                }
                                last
                            />
                        </div>

                        {/* Editable section */}
                        <label className="field-label">Nomor HP</label>
                        <input
                            className="field-input"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 13))}
                            inputMode="numeric"
                            placeholder="812xxx"
                        />

                        <label className="field-label" style={{ marginTop: 14 }}>
                            Alamat
                        </label>
                        <textarea
                            className="field-input"
                            style={{ height: 70, padding: 12, resize: 'none', lineHeight: 1.4 }}
                            value={address}
                            onChange={(e) => setAddress(e.target.value)}
                        />

                        <label className="field-label" style={{ marginTop: 14 }}>
                            Kota
                        </label>
                        <input
                            className="field-input"
                            value={city}
                            onChange={(e) => setCity(e.target.value)}
                        />

                        <label className="field-label" style={{ marginTop: 14 }}>
                            Provinsi
                        </label>
                        <input
                            className="field-input"
                            value={province}
                            onChange={(e) => setProvince(e.target.value)}
                        />

                        {msg && (
                            <div className="chip chip-success" style={{ marginTop: 14, alignSelf: 'flex-start' }}>
                                <Icon name="check" size={14} /> {msg}
                            </div>
                        )}
                        {err && (
                            <div className="chip chip-error" style={{ marginTop: 14, alignSelf: 'flex-start' }}>
                                <Icon name="alert" size={14} /> {err}
                            </div>
                        )}

                        <div style={{ flex: 1, minHeight: 20 }} />
                        <button
                            type="button"
                            className="btn btn-primary"
                            style={{ width: '100%', marginTop: 16 }}
                            disabled={saving}
                            onClick={handleSave}
                        >
                            {saving ? <div className="spinner" /> : 'Simpan Perubahan'}
                        </button>
                    </>
                )}
            </div>
        </MobileFrame>
    );
}

function KV({ k, v, last = false }: { k: string; v: string; last?: boolean }) {
    return (
        <div
            style={{
                display: 'flex',
                padding: '10px 0',
                gap: 16,
                borderBottom: last ? 'none' : '1px solid var(--ink-100)',
            }}
        >
            <div style={{ fontSize: 12, color: 'var(--ink-500)', flex: '0 0 40%' }}>{k}</div>
            <div
                style={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: 'var(--ink-900)',
                    flex: 1,
                    textAlign: 'right',
                    wordBreak: 'break-word',
                }}
            >
                {v}
            </div>
        </div>
    );
}
